"use client";

import { Component, useEffect, useMemo, useState } from "react";
import type { ErrorInfo, ReactNode } from "react";
import {
  CheckCircle2,
  CircleAlert,
  Database,
  MessageSquareText,
  PenLine,
  RefreshCw,
  ShieldCheck,
  Tags,
  Wifi
} from "lucide-react";
import { useStudio } from "./StudioContext";
import { studioApi } from "./lib/api";
import { smsPhoneState } from "./lib/phone";

interface SmsStatus {
  provider: "ClickSend";
  status: "ready" | "not_configured" | "protection_required" | "unavailable";
  configured: boolean;
  balance: number | null;
  currency: string | null;
}

type ProbeState = "idle" | "checking" | "complete" | "partial";

function statusLabel(status: "ok" | "warning" | "error") {
  return status === "ok" ? "Hazır" : status === "warning" ? "Kontrol gerekli" : "Bağlantı yok";
}

export class SystemHealthBoundary extends Component<
  { children: ReactNode },
  { failed: boolean; retryKey: number }
> {
  state = { failed: false, retryKey: 0 };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // Hata bu sayfanın içinde tutulur; portalın geri kalanı çalışmaya devam eder.
  }

  private retry = () => {
    this.setState((current) => ({
      failed: false,
      retryKey: current.retryKey + 1
    }));
  };

  render() {
    if (this.state.failed) {
      return (
        <section className="system-page-fallback" role="alert">
          <CircleAlert size={30} />
          <span className="eyebrow">Sistem kontrolü</span>
          <h1>Kontrol ekranı hazırlanamadı</h1>
          <p>Portalın diğer bölümleri çalışmaya devam ediyor. Bağlantıları yeniden kontrol etmek için tekrar deneyin.</p>
          <button className="button primary" onClick={this.retry}>
            <RefreshCw size={17} /> Yeniden dene
          </button>
        </section>
      );
    }

    return <div key={this.state.retryKey}>{this.props.children}</div>;
  }
}

export function SystemHealthPage() {
  const {
    customers,
    packagePlans,
    staffGoals,
    connectionState,
    lastSyncedAt,
    refresh
  } = useStudio();
  const [sms, setSms] = useState<SmsStatus | null>(null);
  const [checking, setChecking] = useState(false);
  const [probeState, setProbeState] = useState<ProbeState>("idle");
  const [lastCheckedAt, setLastCheckedAt] = useState<Date | null>(null);
  const [probeMessage, setProbeMessage] = useState("Bağlantılar ilk kez kontrol ediliyor.");
  const [serviceWorkerReady, setServiceWorkerReady] = useState(false);

  const googleGoal = staffGoals.find((goal) => goal.metricCode === "GOOGLE_REVIEWS");
  const invalidSmsPhones = useMemo(
    () => customers.filter((customer) => !customer.dontSendSms && smsPhoneState(customer.phone) !== "valid").length,
    [customers]
  );
  const pricedPlanCount = packagePlans.filter((plan) => plan.isActive && plan.defaultPrice != null).length;
  const smsBalance = sms?.balance == null ? null : Number(sms.balance);

  async function checkConnections() {
    if (checking) return;
    setChecking(true);
    setProbeState("checking");
    setProbeMessage("Ana veriler ve mesaj servisi birbirinden bağımsız kontrol ediliyor.");

    const [workspaceResult, smsResult] = await Promise.allSettled([
      refresh(),
      studioApi.command<SmsStatus>("GET_SMS_STATUS", {})
    ]);

    if (smsResult.status === "fulfilled") {
      setSms(smsResult.value.result);
    } else {
      setSms({
        provider: "ClickSend",
        status: "unavailable",
        configured: false,
        balance: null,
        currency: null
      });
    }

    const partial = workspaceResult.status === "rejected" || smsResult.status === "rejected";
    setProbeState(partial ? "partial" : "complete");
    setProbeMessage(
      partial
        ? "Kontrol tamamlandı; yanıt vermeyen bağlantının ayrıntısı ilgili kartta gösteriliyor."
        : "Kontrol tamamlandı. Güncel durum aşağıdaki kartlara işlendi."
    );
    setLastCheckedAt(new Date());
    setChecking(false);
  }

  useEffect(() => {
    setServiceWorkerReady(Boolean(navigator.serviceWorker?.controller));
    void checkConnections();
  }, []);

  const checks = [
    {
      title: "Portal veri bağlantısı",
      detail: connectionState === "live"
        ? `Son güncelleme ${lastSyncedAt ? new Date(lastSyncedAt).toLocaleString("tr-TR") : "az önce"}`
        : "Ana veri bağlantısı yenilenemedi.",
      status: connectionState === "live" ? "ok" as const : "error" as const,
      icon: Database
    },
    {
      title: "Fiyat kataloğu",
      detail: pricedPlanCount
        ? `${pricedPlanCount} aktif paket fiyatı SQL'den geliyor.`
        : "Fiyat kataloğu scripti henüz çalıştırılmamış.",
      status: pricedPlanCount ? "ok" as const : "warning" as const,
      icon: Tags
    },
    {
      title: "ClickSend SMS",
      detail: checking && !sms
        ? "Mesaj servisinin bağlantısı kontrol ediliyor."
        : sms?.status === "ready"
        ? `Bağlantı hazır${smsBalance == null || !Number.isFinite(smsBalance) ? "" : ` · ${smsBalance.toFixed(2)} ${sms.currency ?? ""}`}`
        : sms?.status === "not_configured"
          ? "ClickSend bilgileri tanımlanmamış."
          : sms?.status === "protection_required"
            ? "SMS tekrar koruması için 29 numaralı SQL migration bekleniyor."
          : "ClickSend bağlantısı şu anda doğrulanamadı.",
      status: sms?.status === "ready"
        ? "ok" as const
        : sms?.status === "unavailable"
          ? "error" as const
          : "warning" as const,
      icon: MessageSquareText
    },
    {
      title: "Google yorum sayacı",
      detail: googleGoal
        ? `Manuel takip etkin · dönem sonucu ${googleGoal.currentValue}. Yeni yorum geldikçe +1 yorum kullanılır.`
        : "Bu ay için Google yorum hedefi henüz oluşturulmamış.",
      status: googleGoal ? "ok" as const : "warning" as const,
      icon: PenLine
    },
    {
      title: "SMS telefon uygunluğu",
      detail: invalidSmsPhones
        ? `${invalidSmsPhones} SMS izinli müşterinin telefonu eksik veya geçersiz.`
        : "SMS izinli müşterilerin telefonları gönderime uygun.",
      status: invalidSmsPhones ? "warning" as const : "ok" as const,
      icon: ShieldCheck
    },
    {
      title: "PWA veri güvenliği",
      detail: serviceWorkerReady
        ? "Uygulama kabuğu hazır; SQL API yanıtları çevrimdışı önbelleğe alınmıyor."
        : "Tarayıcı kurulumu geliştirme modunda veya PWA henüz yeniden açılmadı.",
      status: serviceWorkerReady || import.meta.env.DEV ? "ok" as const : "warning" as const,
      icon: Wifi
    }
  ];

  return (
    <>
      <div className="page-heading system-heading">
        <div>
          <span className="eyebrow">Canlı bağlantılar · son kontrol</span>
          <h1>Sistem kontrolü</h1>
          <p>SQL, fiyat kataloğu, SMS, Google yorumları ve PWA durumunu tek ekrandan izleyin.</p>
        </div>
        <button className="button primary" onClick={() => void checkConnections()} disabled={checking}>
          <RefreshCw size={17} className={checking ? "spin" : ""} />
          {checking ? "Kontrol ediliyor…" : "Şimdi kontrol et"}
        </button>
      </div>
      <div className={`health-run-state ${probeState}`} role="status" aria-live="polite">
        <span className="health-run-icon">
          {checking ? <RefreshCw size={18} className="spin" /> : probeState === "partial" ? <CircleAlert size={18} /> : <CheckCircle2 size={18} />}
        </span>
        <div>
          <strong>
            {checking
              ? "Kontrol sürüyor"
              : probeState === "partial"
                ? "Kontrol kısmen tamamlandı"
                : "Sistem durumu güncel"}
          </strong>
          <span>{probeMessage}</span>
        </div>
        {lastCheckedAt && (
          <time dateTime={lastCheckedAt.toISOString()}>
            {lastCheckedAt.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
          </time>
        )}
      </div>
      <section className="health-hero">
        <div>
          <span className="health-pulse"><CheckCircle2 size={20} /></span>
          <div><span className="eyebrow">THH Portal</span><h2>{checks.filter((check) => check.status === "ok").length}/{checks.length} bağlantı hazır</h2></div>
        </div>
        <p>Uyarılar veri silmez veya mevcut işlemleri değiştirmez; yalnızca tamamlanması gereken bağlantıları gösterir.</p>
      </section>
      <div className="health-grid">
        {checks.map((check) => {
          const Icon = check.icon;
          return (
            <article className={`health-card ${check.status}`} key={check.title}>
              <span className="health-icon"><Icon size={21} /></span>
              <div><span>{statusLabel(check.status)}</span><h3>{check.title}</h3><p>{check.detail}</p></div>
              {check.status === "ok" ? <CheckCircle2 size={19} /> : <CircleAlert size={19} />}
            </article>
          );
        })}
      </div>
    </>
  );
}
